import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Field } from "@/components/kit";

export default function AuthPage() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [f, setF] = useState({ email: "", password: "", first_name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // The public login form must be crawlable so Google can read noindex.
  useEffect(() => {
    const existing = document.head.querySelector('meta[name="robots"]');
    const previous = existing?.getAttribute("content");
    const meta = existing || document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex, follow");
    if (!existing) document.head.appendChild(meta);
    return () => {
      if (!existing) meta.remove();
      else if (previous === null) meta.removeAttribute("content");
      else meta.setAttribute("content", previous);
    };
  }, []);
  if (user) return <Navigate to="/" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post(mode === "login" ? "/auth/login" : "/auth/register", mode === "login" ? { email: f.email, password: f.password } : { ...f, ref: localStorage.getItem("lbc_ref") || undefined });
      await refresh();
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-black">
      <div className="hidden lg:flex flex-col justify-between p-12 grid-bg border-r border-[rgba(124,255,0,.12)] relative overflow-hidden">
        <div className="font-display font-black text-2xl">LBC<span className="text-[#7CFF00]"> Agent</span></div>
        <div>
          <div className="mono text-xs tracking-[.3em] text-[#7CFF00] mb-4">ANALYSER → DÉCIDER → AGIR → APPRENDRE</div>
          <h1 className="text-5xl xl:text-6xl font-black tracking-tight leading-[1.02]">Plus qu'un bot.<br /><span className="text-[#7CFF00]">Ton Agent Leboncoin.</span></h1>
          <p className="text-[#A0AAB2] mt-6 max-w-md">Les autres automatisent des tâches. LBC Agent automatise les décisions — avec des permissions explicites, un journal complet et zéro dépendance technique à une marketplace.</p>
        </div>
        <div className="mono text-[10px] text-[#626E77] tracking-widest">28 MODULES · COPILOT · ASSISTANT · AUTOPILOT</div>
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-[#7CFF00]/10 blur-3xl" />
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4 fade-up" data-testid="auth-form">
          <h2 className="text-3xl font-black">{mode === "login" ? "Connexion" : "Créer un compte"}</h2>
          {mode === "register" && <Field label="Prénom"><input data-testid="auth-first-name" className="inp" value={f.first_name} onChange={set("first_name")} required /></Field>}
          <Field label="Email"><input data-testid="auth-email" type="email" className="inp" value={f.email} onChange={set("email")} required /></Field>
          <Field label="Mot de passe"><input data-testid="auth-password" type="password" className="inp" value={f.password} onChange={set("password")} required minLength={mode === "register" ? 8 : 1} /></Field>
          {err && <div className="text-[#FF453A] text-sm" data-testid="auth-error">{err}</div>}
          <Btn className="w-full" disabled={busy} data-testid="auth-submit-btn">{busy ? "…" : mode === "login" ? "Se connecter" : "Créer mon compte"}</Btn>
          {mode === "login" && <button type="button" className="text-xs text-[#626E77] hover:text-[#7CFF00] w-full" data-testid="auth-forgot-btn" onClick={async () => {
            if (!f.email) return setErr("Saisis ton email puis clique sur « Mot de passe oublié ».");
            try { setErr(""); toast.success((await api.post("/auth/forgot-password", { email: f.email })).data.message); } catch (x) { setErr(errMsg(x)); }
          }}>Mot de passe oublié ?</button>}
          <button type="button" className="text-sm text-[#A0AAB2] hover:text-[#7CFF00] w-full" data-testid="auth-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}>
            {mode === "login" ? "Pas de compte ? S'inscrire" : "Déjà un compte ? Se connecter"}
          </button>
        </form>
      </div>
    </div>
  );
}
